import configs from "../../configs.js";
import { getAuthHeaders } from "../../ui/utils.js";
import { persistenceApiClient } from "../../api/PersistenceApiClient.js";
import { uploadClaimFields } from "./uploadClaim.js";

export async function getSignedUrl({
  file,
  folder,
  apiUrl,
  workspaceId = null,
  ACL = "public-read",
  contentType = null,
  delegatedAssetSlotId = null,
  delegatedAssetPurpose = null
}) {
  // Check if workspace is available and use workspace-scoped endpoint
  const useWorkspace = !!workspaceId;

  // Ensure we have a base URL without trailing /files
  const baseUrl = apiUrl.endsWith("/files") ? apiUrl.replace(/\/files$/, "") : apiUrl.replace(/\/$/, "");

  // Build URL based on whether workspace is available
  const url = useWorkspace
    ? `${baseUrl}/files/workspaces/${workspaceId}/get-upload-signed-url`
    : `${baseUrl}/files/get-upload-signed-url`;

  const randomUUID = crypto.randomUUID();
  const authHeaders = await getAuthHeaders(url, "POST");

  // Extract the file extension
  const fileExtension = file.name.split(".").pop();
  // Extract the file name without extension
  const fileNameWithoutExtension = file.name.substring(0, file.name.length - fileExtension.length - 1);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      ...authHeaders
    },
    body: JSON.stringify({
      filename: `${fileNameWithoutExtension}-${randomUUID}.${fileExtension}`.replace(/ /g, "_"),
      folder: folder,
      content_type: contentType || file.type,
      ACL: ACL,
      size: file.size,
      ...(delegatedAssetSlotId
        ? {
            delegated_asset_slot_id: delegatedAssetSlotId,
            delegated_asset_purpose: delegatedAssetPurpose
          }
        : {})
    })
  });

  if (!response.ok) {
    const errorText = await response.text();
    const error = new Error(`Upload failed ${response.status} ${errorText}`);
    error.status = response.status;
    try {
      const json = JSON.parse(errorText);
      error.response = { data: json };
    } catch (e) {
      // ignore json parse error
    }
    throw error;
  }

  const json = await response.json();

  return json;
}

function resolveUploadWorkspaceId(useDefaultWorkspace = false) {
  if (useDefaultWorkspace) {
    return persistenceApiClient.getDefaultWorkspaceId();
  }
  return persistenceApiClient.getWorkspaceId();
}

/**
 * Upload a file to the storage and register it in the backend.
 * No UI, no thumbnail generation.
 * @param {Object} params
 * @param {File} params.file - The file to upload.
 * @param {string} params.token - Auth token.
 * @param {string} params.folder - Target folder.
 * @param {string} params.apiUrl - Base API URL (e.g. configs.PERSISTENCE_API_URL).
 * @param {AbortSignal} [params.abortSignal] - Optional abort signal.
 * @returns {Promise<Object>} - The uploaded file entry.
 */
export async function uploadFile({
  file,
  folder,
  apiUrl = configs["PERSISTENCE_API_URL"],
  abortSignal = null,
  hidden = 0,
  asset_type = "my-media",
  useWorkspaceAssets = false,
  useDefaultWorkspace = false,
  ACL = "public-read",
  delegatedAssetSlotId = null,
  delegatedAssetPurpose = null
}) {
  if (Boolean(delegatedAssetSlotId) !== Boolean(delegatedAssetPurpose)) {
    throw new Error("A delegated Asset upload requires both its slot id and purpose.");
  }

  const workspaceId = resolveUploadWorkspaceId(useDefaultWorkspace);

  // Get signed URL for upload
  const signedUrl = await getSignedUrl({
    file,
    folder,
    apiUrl: `${apiUrl}/files`,
    workspaceId,
    ACL,
    delegatedAssetSlotId,
    delegatedAssetPurpose
  });

  // Upload the file to storage
  await fetch(signedUrl.presigned_url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
      "x-ms-blob-type": "BlockBlob"
    },
    body: file,
    signal: abortSignal
  });

  // Prepare entry metadata
  const fileUrl = signedUrl.download_url;
  const fileNameParts = file.name.split(".");
  const extension = fileNameParts.pop();
  const name = fileNameParts.join(".");
  const fileName = `${name}.${extension}`;

  // Use the key returned by the API as the path, or fall back to URL parsing
  const path = signedUrl.key || new URL(fileUrl).pathname.replace(/^\/nextrealitydigital\//, "");

  const entry = {
    name: fileName,
    title: fileName,
    path,
    type: extension,
    asset_type,
    size: file.size,
    content_type: file.type,
    url: fileUrl,
    hidden,
    ...(delegatedAssetSlotId
      ? {
          delegated_asset_slot_id: delegatedAssetSlotId,
          delegated_asset_purpose: delegatedAssetPurpose
        }
      : {}),
    ...uploadClaimFields(signedUrl)
  };

  // Register the file in the backend
  const myMediaUrl =
    useWorkspaceAssets && workspaceId ? `${apiUrl}/workspaces/${workspaceId}/assets` : `${apiUrl}/assets`;
  const authHeaders = await getAuthHeaders(myMediaUrl, "POST");
  const response = await fetch(myMediaUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      ...authHeaders
    },
    body: JSON.stringify(entry)
  });

  if (!response.ok) {
    const error = new Error(`Backend registration failed: ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  const json = await response.json();

  return {
    ...entry,
    id: json.data.id,
    uuid: json.data.uuid ?? null
  };
}

/**
 * Short version of uploadFile: only uploads and returns the download URL.
 * @param {Object} params
 * @param {File} params.file - The file to upload.
 * @param {string} params.token - Auth token.
 * @param {string} params.folder - Target folder.
 * @param {string} params.apiUrl - Base API URL (e.g. configs.PERSISTENCE_API_URL).
 * @param {AbortSignal} [params.abortSignal] - Optional abort signal.
 * @returns {Promise<{url: string}>} - The download URL of the uploaded file.
 */
export async function uploadFileShort({
  file,
  folder,
  apiUrl = configs["PERSISTENCE_API_URL"],
  abortSignal = null,
  useDefaultWorkspace = false
}) {
  const workspaceId = resolveUploadWorkspaceId(useDefaultWorkspace);

  // Get signed URL for upload
  const signedUrl = await getSignedUrl({
    file,
    folder,
    apiUrl: `${apiUrl}/files`,
    workspaceId
  });

  // Upload the file to storage
  await fetch(signedUrl.presigned_url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
      "x-ms-blob-type": "BlockBlob"
    },
    body: file,
    signal: abortSignal
  });

  return { url: signedUrl.download_url };
}
